import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { RevealLines, FadeUp } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import EditorialMarquee from "@/components/Marquee";

const HERO = "https://images.unsplash.com/photo-1777888740879-bd206adc84fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBmYXNoaW9uJTIwbW9kZWwlMjBlZGl0b3JpYWx8ZW58MHx8fHwxNzg2MTY1NjY3fDA&ixlib=rb-4.1.0&q=85";
const TEXTURE = "https://images.unsplash.com/photo-1629735919597-fed920b5bd84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBhcmNoaXRlY3R1cmUlMjBkZXRhaWx8ZW58MHx8fHwxNzg2MTY1Njc1fDA&ixlib=rb-4.1.0&q=85";
const ATELIER = "https://images.unsplash.com/photo-1619002117199-47c7f0427d21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYXNoaW9uJTIwbW9kZWwlMjBlZGl0b3JpYWx8ZW58MHx8fHwxNzg2MTY1NjY3fDA&ixlib=rb-4.1.0&q=85";

const CHAPTERS = [
  { n: "01", t: "One thread, one spirit", b: "TAMASHii is built on the belief that clothing carries spirit. We celebrate the unrestrained colour, craft and heritage of the Indian subcontinent." },
  { n: "02", t: "Genderless by design", b: "Cloth has no gender. Every silhouette is drafted to be worn by anyone who feels its pull. Fit is intent, not category." },
  { n: "03", t: "Made in limited runs", b: "Handloom fabric, natural dye, small batches. When a piece is gone, it is gone. We refuse the logic of endless supply." },
];

const Home = () => {
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative h-screen overflow-hidden bg-[#0A0A0C]">
        <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
          <img src={HERO} alt="Tamashii" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-[#0A0A0C]/60" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative h-full max-w-[1600px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <motion.p
            className="overline mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
          >
            Unisex Ethnic Wear · Est. 2026
          </motion.p>
          <RevealLines
            tag="h1"
            immediate
            lines={["Wear the", "spirit of", "the cloth."]}
            className="font-serif text-[18vw] md:text-[13vw] leading-[0.82] tracking-tighter text-bone"
            delay={0.2}
          />
          <div className="flex flex-col md:flex-row md:items-end justify-between mt-10 gap-8">
            <motion.p
              className="text-ash max-w-md leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9 }}
            >
              A genderless ethnic label rooted in Indian craft and colour. Draped, dyed and made by hand.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.9 }}
            >
              <Link to="/shop" data-testid="hero-shop-cta" className="btn-fill inline-flex items-center gap-3 px-10 py-4 overline text-sm">
                Enter the Collection <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ash"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowDown size={20} weight="thin" />
        </motion.div>
      </section>

      <EditorialMarquee words={["Handloom", "TAMASHii", "Genderless", "Limited Runs", "Naturally Dyed"]} />

      {/* MANIFESTO */}
      <section id="manifesto" className="relative py-28 md:py-40 overflow-hidden">
        <img src={TEXTURE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.06] pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-5 md:px-10">
          <FadeUp>
            <p className="overline mb-4">The Manifesto</p>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-y-16 gap-x-12 mt-10">
            {CHAPTERS.map((c, i) => (
              <div key={c.n} className={`md:col-span-4 ${i === 1 ? "md:mt-24" : ""} ${i === 2 ? "md:mt-12" : ""}`}>
                <FadeUp delay={i * 0.08}>
                  <span className="font-mono text-crimson text-sm">{c.n} /</span>
                  <h3 className="font-serif text-4xl md:text-5xl text-bone mt-4 leading-tight">{c.t}</h3>
                  <p className="text-ash mt-5 leading-relaxed">{c.b}</p>
                </FadeUp>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-16 md:py-24 max-w-[1600px] mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between mb-14 border-b border-line pb-8">
          <RevealLines tag="h2" lines={["Featured", "Pieces"]} className="font-serif text-6xl md:text-8xl text-bone leading-[0.85] tracking-tight" />
          <Link to="/shop" className="overline link-underline hidden md:flex items-center gap-2 pb-2">
            View All <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-16">
          {featured.map((p, i) => (
            <ProductCard
              key={p.id}
              product={p}
              index={i}
              className={i === 0 ? "md:col-span-7" : i === 1 ? "md:col-span-5" : "md:col-span-4"}
            />
          ))}
        </div>
      </section>

      {/* ATELIER STORY */}
      <section id="atelier" className="py-16 md:py-28 max-w-[1600px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <FadeUp className="md:col-span-6">
            <div className="img-zoom aspect-[4/5]">
              <img src={ATELIER} alt="Atelier" className="w-full h-full object-cover" />
            </div>
          </FadeUp>
          <div className="md:col-span-5 md:col-start-8">
            <FadeUp>
              <p className="overline mb-6">The Atelier</p>
              <h2 className="font-serif text-5xl md:text-6xl text-bone leading-[0.95]">
                Every piece begins as a <span className="italic text-gold">single</span> thread.
              </h2>
              <p className="text-ash mt-8 leading-relaxed">
                In ateliers across India, our makers draw on generations of handloom knowledge.
                We dye with madder root and marigold, cut with a clean, architectural hand, and finish with the ornament of the subcontinent.
              </p>
              <Link to="/shop" className="btn-fill inline-flex items-center gap-3 px-10 py-4 overline text-sm mt-10">
                Shop the Collection <ArrowRight size={16} />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
